import { forwardRef, MouseEvent, PropsWithChildren } from 'react';
import { HasClassName } from '~/types/HasClassName';
import { HasStyle } from '~/types/HasStyle';
import { Loader } from '../Loader/Loader';
import styles from './Button.module.css';
import classNames from 'classnames';

type RootProps = PropsWithChildren &
  HasClassName &
  HasStyle & {
    onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
    variant?: 'primary' | 'secondary' | 'destructive';
    type?: 'button' | 'submit';
    isLoading?: boolean;
    disabled?: boolean;
    square?: boolean;
    block?: boolean;
  };

type GroupProps = PropsWithChildren & {
  align?: 'left' | 'right';
  neutral?: boolean;
};

const Root = forwardRef<HTMLButtonElement, RootProps>(function Root(props, ref) {
  const {
    children,
    type = 'button',
    variant = 'primary',
    disabled = false,
    block = false,
    isLoading = false,
    square = false,
    className,
    style,
    ...rest
  } = props;

  return (
    <button
      className={classNames(
        styles.button,
        styles[variant],
        {
          [styles.square]: square,
          [styles.block]: block,
        },
        className
      )}
      disabled={isLoading || disabled}
      style={style}
      type={type}
      ref={ref}
      {...rest}
    >
      {children}

      {isLoading && (
        <span className={styles.loading}>
          <Loader variant="light" />
        </span>
      )}
    </button>
  );
});

function Group(props: GroupProps) {
  const { children, align = 'left', neutral = false } = props;

  return (
    <div
      className={classNames(styles.buttonGroup, styles[`align-${align}`], {
        [styles.neutral]: neutral,
      })}
    >
      {children}
    </div>
  );
}

export const Button = Object.assign(Root, {
  Group,
});
